/* @flow */

import shaka from 'shaka-player';

import type { DmsSettingMap } from '../../../libs/netgemLibrary/dms/types/DeviceInfoSettings';

import { logError, logInfo, logWarning } from '../../../helpers/debug/debug';
import { DRM_KEY_SYSTEM } from '../../../helpers/jsHelpers/Drm';
import { wait } from '../../../helpers/jsHelpers/promise';
import { canParseUrl } from '../../../helpers/jsHelpers/url';
import { getSettingValueByNameFromDeviceSettings } from '../../../redux/netgemApi/actions/helpers/settings';
import { type ShakaConfiguration, type ShakaError } from '../implementation/shakaTypes';

// Playing duration for DRM diagnosis in debug mode (otherwise it stops as soon as success or failure is detected)
const DEBUG_VISIBILITY_DURATION = 5_000;

class DrmDiagnosis {
  isAvailable: boolean;

  drmDiagnosticLAUrl: string;

  drmDiagnosticStreamUrl: string;

  isCleaning: boolean;

  isDebugModeEnabled: boolean;

  player: typeof shaka.Player;

  promise: Promise<boolean>;

  returnResult: (result: boolean) => void;

  videoElement: HTMLVideoElement;

  videoDiagContainer: HTMLElement;

  constructor(dmsSettings: DmsSettingMap, isDebugModeEnabled: boolean) {
    this.isCleaning = false;
    this.isDebugModeEnabled = isDebugModeEnabled;

    const streamUrl = getSettingValueByNameFromDeviceSettings(dmsSettings, 'video.drm.diagnostic', 'streamUrl');
    const laUrl = getSettingValueByNameFromDeviceSettings(dmsSettings, 'video.drm.diagnostic', 'laUrl');

    if (window.safari !== undefined) {
      // Diagnostic not available for Fairplay (Safari): no check will be performed
      this.isAvailable = false;
      logInfo('DRM check will not be performed because Fairplay diagnostic is not implemented');
      return;
    }

    // The null check is redundant, but Flowjs needs it for the assignment to members drmDiagnosticStreamUrl and drmDiagnosticLAUrl
    if (streamUrl === null || laUrl === null || !canParseUrl(streamUrl)) {
      // Diagnostic URLs not defined: no check will be performed
      this.isAvailable = false;
      logWarning('DRM check will not be performed because at least one of the 2 URLs is invalid');
      return;
    }

    this.isAvailable = true;
    this.drmDiagnosticStreamUrl = streamUrl;
    this.drmDiagnosticLAUrl = laUrl;

    // Create promise that will be returned (with true/false value, depending on the result)
    this.promise = new Promise((resolve) => {
      this.returnResult = resolve;
    });

    this.initialize();
  }

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'DrmDiagnosis';
  }

  initialize: () => void = () => {
    this.videoDiagContainer = document.createElement('div');
    this.videoDiagContainer.classList.add('videoDiagContainer', this.isDebugModeEnabled ? 'visible' : 'hidden');
    const videoDiagContent = document.createElement('div');
    videoDiagContent.className = 'videoDiagContent';
    this.videoElement = document.createElement('video');
    const videoDiagLegend = document.createElement('div');
    videoDiagLegend.className = 'videoDiagLegend';
    videoDiagLegend.innerHTML = 'Diagnostic vidéo';
    videoDiagContent.append(this.videoElement);
    videoDiagContent.append(videoDiagLegend);
    this.videoDiagContainer.append(videoDiagContent);

    const body = document.querySelector('body');
    body?.append(this.videoDiagContainer);

    // Install built-in polyfills to patch browser incompatibilities
    shaka.polyfill.installAll();

    this.player = new shaka.Player();
  };

  cleanUpNotify: (errorMessage?: string) => Promise<void> = async (errorMessage) => {
    this.isCleaning = true;
    this.player.removeEventListener('error', this.errorEvent, { once: true, passive: true });
    this.videoElement.removeEventListener('playing', this.playingEvent, { once: true, passive: true });

    if (errorMessage) {
      logError(errorMessage);
    }

    if (this.isDebugModeEnabled) {
      logInfo(`DRM diagnosis result: ${errorMessage ? 'failure' : 'success'}`);
      await wait(DEBUG_VISIBILITY_DURATION);
    }

    try {
      await this.player.detach();
      await this.player.destroy();
    } catch {
      if (!errorMessage) {
        logError('Error detaching videoElement or destroying player');
      }
    } finally {
      this.videoElement.remove();
      this.videoDiagContainer.remove();
    }
  };

  // Video not working: DRM not supported
  errorEvent: (event: ShakaError) => Promise<void> = async (event) => {
    const {
      detail: {
        code,
        data: [untypedMediaErrorCode],
      },
    } = event;

    const mediaErrorCode = typeof untypedMediaErrorCode === 'number' ? untypedMediaErrorCode : 0;

    await this.cleanUpNotify(`Error playing (${code}.${mediaErrorCode})`);
    this.returnResult(false);
  };

  // Video working: DRM supported
  playingEvent: () => Promise<void> = async () => {
    await this.cleanUpNotify();
    this.returnResult(true);
  };

  runDiagnosis: () => Promise<boolean> = async () => {
    if (!this.isAvailable) {
      // Diagnostic URLs not defined: no check will be performed
      return true;
    }

    if (!this.player) {
      // Video not working: DRM not supported
      await this.cleanUpNotify('Cannot instantiate player');
      return false;
    }

    const configuration: ShakaConfiguration = {
      drm: {
        advanced: {
          [DRM_KEY_SYSTEM.Widevine]: {
            audioRobustness: ['SW_SECURE_CRYPTO'],
            videoRobustness: ['SW_SECURE_CRYPTO'],
          },
        },
        initDataTransform: (initData) => initData,
        servers: { [DRM_KEY_SYSTEM.Widevine]: this.drmDiagnosticLAUrl },
      },
    };

    try {
      await this.player.attach(this.videoElement);

      if (!this.player.configure(configuration)) {
        // Video not working: DRM not supported
        await this.cleanUpNotify('Error configuring player');
        return false;
      }
    } catch {
      // Video not working: DRM not supported
      await this.cleanUpNotify('Error initializing player');
      return false;
    }

    this.player.addEventListener('error', this.errorEvent, { once: true, passive: true });
    this.videoElement.addEventListener('playing', this.playingEvent, { once: true, passive: true });

    try {
      await this.player.load(this.drmDiagnosticStreamUrl);
      await this.videoElement.play();
    } catch (error) {
      logError(error);

      if (!this.isCleaning) {
        /*
         * If error code is 6001, we're probably trying to play a Widevine-protected stream on an Apple device,
         * so no error is displayed and support is considered OK
         */
        const errorMsg = error instanceof Error ? `Error in play() promise (${error.message})` : `Error in play() promise (code: ${error.message})`;

        // Video not working: DRM not supported
        await this.cleanUpNotify(errorMsg);
        this.returnResult(false);
      }
    }

    return this.promise;
  };
}

export default DrmDiagnosis;
